import { motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import {
  Course,
  Module,
  Program,
  Project,
  QuizListType,
} from "utils/interfaces";
import ProgramCard from "../utils/cards/ProgramCard";
import CourseCard from "../utils/cards/CourseCard";
import ProjectCard from "../utils/cards/ProjectCard";
import ModuleCard from "../utils/cards/ModuleCard";
import QuizzesList from "../utils/quizzes-list";
import EmptyView from "../utils/components/EmptyView";
import { LoadingDots } from "utils/loadingAnimation";
import lessonInformation from "../utils/functions/lessonInformation";
import { MainContext } from "components/utils/Contexts";

interface LessonsListParams {
  lessonListToShow: string;
  programs: Program[] | null;
  courses: Course[] | null;
  projects: Project[] | null;
  modules: Module[] | null;
  searchQuery: string;
  selectedData: Program | Course | Project | Module | null;
  navRegister: {
    lessonListToShow: string;
    selectedData: Program | Course | Project | Module | null;
    record: Program | Course | Project | Module | null;
  }[];
  handleExpandClick: (
    record: Program | Course | Project | Module
  ) => () => void;
  handleEditClick: (
    record: Program | Course | Project | Module
  ) => (event: React.MouseEvent) => void;
  handleDeleteClick: (
    record: Program | Course | Project | Module,
    lessonType: string
  ) => (event: React.MouseEvent) => void;
  handleAddToClasses: (
    record: Program | Course | Project | Module,
    lesson: string
  ) => (event: React.MouseEvent) => void;
  setLinkedId: React.Dispatch<React.SetStateAction<string>>;
  setLinkedLesson: React.Dispatch<React.SetStateAction<string>>;
  setShowEditQuiz: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedQuizToEdit: React.Dispatch<
    React.SetStateAction<QuizListType | null>
  >;
  showEditQuiz: boolean;
  pressBackSignal: boolean;
  setPressBackSignal: React.Dispatch<React.SetStateAction<boolean>>;
  onCreateLesson: (
    lesson: "programs" | "courses" | "projects" | "modules"
  ) => void;
}

const LessonsList: React.FC<LessonsListParams> = ({
  lessonListToShow,
  programs,
  courses,
  projects,
  modules,
  searchQuery,
  selectedData,
  navRegister,
  handleExpandClick,
  handleEditClick,
  handleDeleteClick,
  handleAddToClasses,
  setLinkedId,
  setLinkedLesson,
  setShowEditQuiz,
  setSelectedQuizToEdit,
  showEditQuiz,
  pressBackSignal,
  setPressBackSignal,
  onCreateLesson,
}) => {
  // States definition
  const [previousList, setPreviousList] = useState(lessonListToShow);
  const [renderedPrograms, setRenderedPrograms] = useState(programs);
  const [renderedCourses, setRenderedCourses] = useState(courses);
  const [renderedProjects, setRenderedProjects] = useState(projects);
  const [renderedModules, setRenderedModules] = useState(modules);

  // Get the context values
  const { userRole } = useContext(MainContext);

  /**
   * Hook to define when to update the cards
   */
  useEffect(() => {
    // If the press back signal is deactivated, then render
    if (!pressBackSignal) {
      setRenderedPrograms(programs);
      setRenderedCourses(courses);
      setRenderedProjects(projects);
      setRenderedModules(modules);
    }
  }, [programs, courses, projects, modules, pressBackSignal]);

  /**
   * Define the previous list
   */
  useEffect(() => {
    setPreviousList(lessonListToShow);
  }, [lessonListToShow]);

  /**
   * Function used to create new from the empty view button
   */
  const handleCreateNewFromEmptyView = (selectedLesson: string) => {
    // If we are creating then, define the lesson to be added as child
    if (selectedData) {
      setLinkedId(selectedData ? selectedData._id : "");
      setLinkedLesson(lessonInformation(selectedLesson).parent);
    }

    // Select create new based on the selectedLesson
    onCreateLesson(
      selectedLesson as "programs" | "courses" | "projects" | "modules"
    );
  };

  /**
   * Function that defines the criterion used for filtering
   * data by the search text
   * @param item
   * @returns
   */
  const textCriterion = (searchObjective: string) => {
    // And return the veredict
    return searchObjective.toLowerCase().includes(searchQuery.toLowerCase());
  };

  return (
    <div className="relative w-full h-full">
      {/* Programs */}
      <motion.div
        initial={{
          x: "0%",
        }}
        animate={{
          x: lessonListToShow === "programs" ? "0%" : "-100%",
          opacity: lessonListToShow === "programs" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
        // Force the press back signal to go false to allow rendering in real time
        onAnimationComplete={() => setPressBackSignal(false)}
      >
        {renderedPrograms && renderedPrograms.length > 0 ? (
          renderedPrograms
            .filter((program: Program) =>
              textCriterion(`${program.title} ${program.description}`)
            )
            .map((program: Program, index: number) => (
              <ProgramCard
                key={index}
                program={program}
                handleExpand={handleExpandClick(program)}
                handleEdit={handleEditClick(program)}
                handleDelete={handleDeleteClick(program, "programs")}
              />
            ))
        ) : renderedPrograms === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={[
              "master admin",
              "customer admin",
              "class admin",
            ].includes(userRole)}
            onCreateNewClick={() => handleCreateNewFromEmptyView("programs")}
          />
        )}
      </motion.div>

      {/* Courses */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x:
            previousList === "programs"
              ? "100%"
              : lessonListToShow === "courses"
              ? "0%"
              : "-100%",
          opacity: lessonListToShow === "courses" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
        // Force the press back signal to go false to allow rendering in real time
        onAnimationComplete={() => setPressBackSignal(false)}
      >
        {renderedCourses && renderedCourses.length > 0 ? (
          renderedCourses
            .filter((course) =>
              textCriterion(`${course.title} ${course.description}`)
            )
            .map((course, index) => (
              <CourseCard
                key={index}
                course={course}
                handleExpand={handleExpandClick(course)}
                handleEdit={handleEditClick(course)}
                handleDelete={handleDeleteClick(course, "courses")}
                handleAddToClasses={handleAddToClasses(course, "courses")}
              />
            ))
        ) : renderedCourses === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={[
              "master admin",
              "customer admin",
              "class admin",
            ].includes(userRole)}
            onCreateNewClick={() => handleCreateNewFromEmptyView("courses")}
          />
        )}
      </motion.div>

      {/* Projects */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x:
            previousList === "modules"
              ? "-100%"
              : lessonListToShow === "projects"
              ? "0%"
              : "100%",
          opacity: lessonListToShow === "projects" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
        // Force the press back signal to go false to allow rendering in real time
        onAnimationComplete={() => setPressBackSignal(false)}
      >
        {(renderedProjects && renderedProjects.length > 0) ||
        (selectedData &&
          "quizzes" in selectedData &&
          selectedData.quizzes.length > 0) ? (
          <>
            {renderedProjects &&
              renderedProjects
                .filter((project) =>
                  textCriterion(`${project.title} ${project.description}`)
                )
                .map((project, index) => (
                  <ProjectCard
                    key={index}
                    project={project}
                    handleExpand={handleExpandClick(project)}
                    handleEdit={handleEditClick(project)}
                    handleDelete={handleDeleteClick(project, "projects")}
                    handleAddToClasses={handleAddToClasses(project, "projects")}
                  />
                ))}
            {selectedData &&
              "quizzes" in selectedData &&
              selectedData.quizzes.length > 0 && (
                <QuizzesList
                  searchQuery={""}
                  selectedData={selectedData}
                  navRegister={navRegister}
                  setShowEditQuiz={setShowEditQuiz}
                  setSelectedQuizToEdit={setSelectedQuizToEdit}
                />
              )}
          </>
        ) : renderedProjects === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={[
              "master admin",
              "customer admin",
              "class admin",
            ].includes(userRole)}
            onCreateNewClick={() => handleCreateNewFromEmptyView("projects")}
          />
        )}
      </motion.div>

      {/* Modules */}
      <motion.div
        initial={{
          x: "100%",
        }}
        animate={{
          x: lessonListToShow === "modules" ? "0%" : "100%",
          opacity: lessonListToShow === "modules" ? 1 : 0,
          transition: {
            duration: 0.5,
          },
        }}
        className="w-full h-full flex-col gap-4 inline-flex absolute top-0 left-0 overflow-y-auto custom-scroll"
        // Force the press back signal to go false to allow rendering in real time
        onAnimationComplete={() => setPressBackSignal(false)}
      >
        {renderedModules && renderedModules.length > 0 ? (
          renderedModules
            .filter((module) =>
              textCriterion(`${module.title} ${module.description}`)
            )
            .map((module, index) => (
              <ModuleCard
                key={index}
                module={module}
                handleExpand={handleExpandClick(module)}
                handleEdit={handleEditClick(module)}
                handleDelete={handleDeleteClick(module, "modules")}
                handleAddToClasses={handleAddToClasses(module, "modules")}
              />
            ))
        ) : renderedModules === null ? (
          <div className="w-full h-full flex justify-center items-center">
            <LoadingDots bgColor="bg-neutral-300" />
          </div>
        ) : (
          <EmptyView
            showCreateNew={[
              "master admin",
              "customer admin",
              "class admin",
            ].includes(userRole)}
            onCreateNewClick={() => handleCreateNewFromEmptyView("modules")}
          />
        )}
      </motion.div>
    </div>
  );
};

export default LessonsList;
